import React, { useCallback } from 'react';
import { func, object, string } from 'prop-types';
import classNames from 'classnames';
import { Form as FinalForm, Field } from 'react-final-form';
import { useLocation, useParams } from 'react-router-dom';

import { useIntl, FormattedMessage } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import routeConfiguration from '../../../routing/routeConfiguration';
import { parse } from '../../../util/urlHelpers';
import { useConfiguration } from '../../../context/configurationContext';

import { Form } from '../../../components';
import LocationAutocompleteInputImpl from '../../../components/LocationAutocompleteInput/LocationAutocompleteInput';
import SearchIcon from '../../../components/Topbar/SearchIcon';

import css from './DestinationSearchBar.module.css';

const identity = v => v;

/**
 * DestinationSearchBar
 *
 * Rebuilt search bar matching new-search-ui/search-page-desktop.html: a clean
 * rounded pill with a bold "Reiseziel" label above a "Wo geht's hin?" location
 * input and a teal circular search button. Destination only — no dates/guests.
 *
 * Unlike the previous version this drives LocationAutocompleteInput directly
 * (instead of going through TopbarSearchForm), so it owns ALL of the styling —
 * the input and the predictions dropdown — via its own CSS classes, while
 * reusing the full autocomplete/geocoding/prediction engine unchanged.
 */
const DestinationSearchBar = props => {
  const { rootClassName, className, history, validQueryParams } = props;

  const intl = useIntl();
  const location = useLocation();
  const config = useConfiguration();
  const { lang } = useParams();

  const langFromIntl = intl.locale.trim();
  const classes = classNames(rootClassName || css.root, className);

  const handleSubmit = useCallback(
    values => {
      const keywords = values.keywords;
      const search = values?.location?.search ? values.location.search : '';
      const selectedPlace = values?.location?.selectedPlace ? values.location.selectedPlace : '';
      const { bounds, country, origin } = selectedPlace || {};

      const searchParams = {
        ...validQueryParams,
        ...(keywords ? { keywords } : {}),
        ...(config.sortSearchByDistance && origin ? { origin } : {}),
        address: search,
        country,
        bounds,
      };

      if (search !== '' || selectedPlace !== '') {
        history.push(
          createResourceLocatorString(
            'SearchPage',
            routeConfiguration(),
            { lang },
            searchParams
          )
        );
      } else {
        history.push(`/${langFromIntl}/s`);
      }
    },
    [history, validQueryParams, config, lang, langFromIntl]
  );

  // Pre-fill from the current URL so the field reflects the active search.
  const { address, origin, bounds, country } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const locationFieldsPresent = config.sortSearchByDistance
    ? address && origin && bounds
    : address && bounds;
  const initialValues = {
    location: locationFieldsPresent
      ? {
          search: address,
          selectedPlace: { address, origin, bounds, country: country || validQueryParams?.country },
        }
      : null,
  };

  return (
    <FinalForm
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit: submit }) => {
        // The location field auto-submits when a prediction is selected, so we
        // wire onChange to submit as soon as a place is chosen.
        const onLocationChange = onChange => value => {
          onChange(value);
          if (value && value.selectedPlace) {
            submit();
          }
        };

        return (
          <Form className={classes} onSubmit={e => e.preventDefault()}>
            <div className={css.destinationSegment}>
              <span className={css.segmentLabel}>
                <FormattedMessage id="criteria_header_6" />
              </span>

              <Field
                name="location"
                format={identity}
                render={({ input, meta }) => {
                  const { onChange, ...restInput } = input;
                  const searchInput = { ...restInput, onChange: onLocationChange(onChange) };
                  return (
                    <LocationAutocompleteInputImpl
                      placeholder={intl.formatMessage({ id: 'worldwide' })}
                      /* This bar ships its own teal submit button, so the
                         component's built-in home-page CTA stays hidden. */
                      hideSearchButton={true}
                      iconClassName={css.inputIcon}
                      className={css.locationRoot}
                      inputClassName={css.locationInput}
                      predictionsClassName={css.predictions}
                      closeOnBlur={true}
                      input={searchInput}
                      meta={meta}
                    />
                  );
                }}
              />
            </div>

            <button
              type="button"
              className={css.searchSubmit}
              onClick={submit}
              data-elb="searchp"
              data-elbaction="click: click searchIcon"
              aria-label="Search"
            >
              <SearchIcon rootClassName={css.searchIconMain} />
            </button>
          </Form>
        );
      }}
    />
  );
};

DestinationSearchBar.defaultProps = {
  rootClassName: null,
  className: null,
  validQueryParams: {},
};

DestinationSearchBar.propTypes = {
  rootClassName: string,
  className: string,
  history: object.isRequired,
  validQueryParams: object,
};

export default DestinationSearchBar;
